import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers,
  FiGlobe,
  FiActivity,
  FiSettings,
  FiServer,
  FiTrendingUp,
  FiDollarSign,
  FiAlertCircle
} from 'react-icons/fi';

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const Welcome = styled.div`
  margin-bottom: 2rem;
  
  h1 {
    font-size: 2.5rem;
    color: #333;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    font-size: 1.125rem;
    color: #666;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 3rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: transform 0.2s, box-shadow 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const StatIcon = styled.div<{ color: string }>`
  width: 60px;
  height: 60px;
  background: ${props => props.color}20;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: ${props => props.color};
`;

const StatContent = styled.div`
  flex: 1;
  
  .value {
    font-size: 2rem;
    font-weight: bold;
    color: #333;
    margin-bottom: 0.25rem;
  }
  
  .label {
    color: #666;
    font-size: 0.875rem;
  }
  
  .trend {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    font-size: 0.75rem;
    margin-top: 0.5rem;
    
    &.positive {
      color: #6bcf7f;
    }
    
    &.negative {
      color: #ff6b6b;
    }
  }
`;

const QuickActions = styled.div`
  margin-bottom: 3rem;
  
  h2 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 1.5rem;
  }
`;

const ActionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
`;

const ActionCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    
    .icon {
      background-color: #ff6b6b;
      color: white;
    }
  }
  
  .icon {
    width: 48px;
    height: 48px;
    background: #f0f0f0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
    font-size: 1.5rem;
    color: #ff6b6b;
    transition: all 0.2s;
  }
  
  .title {
    font-weight: 500;
    color: #333;
    margin-bottom: 0.25rem;
  }
  
  .description {
    font-size: 0.75rem;
    color: #666;
  }
`;

const RecentActivity = styled.div`
  h2 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 1.5rem;
  }
`;

const ActivityCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const ActivityHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h3 {
    margin: 0;
    font-size: 1.125rem;
    color: #333;
  }
  
  .view-all {
    color: #ff6b6b;
    font-size: 0.875rem;
    cursor: pointer;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ActivityList = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;

const ActivityItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &:last-child {
    border-bottom: none;
  }
  
  .icon {
    width: 36px;
    height: 36px;
    background: #f0f0f0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #666;
    font-size: 1rem;
  }
  
  .content {
    flex: 1;
    
    .action {
      font-size: 0.875rem;
      color: #333;
      margin-bottom: 0.25rem;
    }
    
    .time {
      font-size: 0.75rem;
      color: #999;
    }
  }
`;

const SuperadminDashboard: React.FC = () => {
  const navigate = useNavigate();

  const stats = [
    {
      icon: FiUsers,
      color: '#4e89e3',
      value: '12',
      label: 'Tenants Ativos',
      trend: { value: '+2', type: 'positive' }
    },
    {
      icon: FiDollarSign,
      color: '#6bcf7f',
      value: 'R$ 4.580',
      label: 'Receita Mensal',
      trend: { value: '+15%', type: 'positive' }
    },
    {
      icon: FiServer,
      color: '#b983ff',
      value: '98.5%',
      label: 'Uptime',
      trend: { value: '-0.5%', type: 'negative' }
    },
    {
      icon: FiAlertCircle,
      color: '#ff6b6b',
      value: '3',
      label: 'Alertas',
      trend: { value: '+1', type: 'negative' }
    }
  ];

  const quickActions = [
    {
      icon: FiUsers,
      title: 'Novo Tenant',
      description: 'Criar nova conta',
      path: '/superadmin/tenants/new'
    },
    {
      icon: FiGlobe,
      title: 'Gerenciar Domínios',
      description: 'Configurar DNS e SSL',
      path: '/superadmin/domains'
    },
    {
      icon: FiActivity,
      title: 'Logs do Sistema',
      description: 'Ver atividades recentes',
      path: '/superadmin/audit'
    },
    {
      icon: FiSettings,
      title: 'Configurações',
      description: 'Sistema e integrações',
      path: '/superadmin/settings'
    }
  ];

  const recentActivity = [
    {
      icon: FiUsers,
      action: 'Novo tenant criado: Imobiliária Central',
      time: 'Há 2 horas'
    },
    {
      icon: FiGlobe,
      action: 'Domínio verificado: centralimob.com.br',
      time: 'Há 4 horas'
    },
    {
      icon: FiTrendingUp,
      action: 'Upgrade de plano: Silva Imóveis (Basic → Pro)',
      time: 'Há 1 dia'
    },
    {
      icon: FiAlertCircle,
      action: 'Certificado SSL renovado: 3 domínios',
      time: 'Há 2 dias'
    }
  ];

  return (
    <Container>
      <Welcome>
        <h1>Dashboard Superadmin</h1>
        <p>Gerencie todos os tenants e configurações do sistema</p>
      </Welcome>

      <StatsGrid>
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <StatCard key={index}>
              <StatIcon color={stat.color}>
                <Icon />
              </StatIcon>
              <StatContent>
                <div className="value">{stat.value}</div>
                <div className="label">{stat.label}</div>
                {stat.trend && (
                  <div className={`trend ${stat.trend.type}`}>
                    <FiTrendingUp />
                    {stat.trend.value}
                  </div>
                )}
              </StatContent>
            </StatCard>
          );
        })}
      </StatsGrid>

      <QuickActions>
        <h2>Ações Rápidas</h2>
        <ActionsGrid>
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <ActionCard key={index} onClick={() => navigate(action.path)}>
                <div className="icon">
                  <Icon />
                </div>
                <div className="title">{action.title}</div>
                <div className="description">{action.description}</div>
              </ActionCard>
            );
          })}
        </ActionsGrid>
      </QuickActions>

      <RecentActivity>
        <h2>Atividade Recente</h2>
        <ActivityCard>
          <ActivityHeader>
            <h3>Últimas Atividades</h3>
            <span className="view-all" onClick={() => navigate('/superadmin/audit')}>
              Ver todas
            </span>
          </ActivityHeader>
          <ActivityList>
            {recentActivity.map((activity, index) => {
              const Icon = activity.icon;
              return (
                <ActivityItem key={index}>
                  <div className="icon">
                    <Icon />
                  </div>
                  <div className="content">
                    <div className="action">{activity.action}</div>
                    <div className="time">{activity.time}</div>
                  </div>
                </ActivityItem>
              );
            })}
          </ActivityList>
        </ActivityCard>
      </RecentActivity>
    </Container>
  );
};

export default SuperadminDashboard;